<template>
  <div v-if="hasQuotes">
    <div class="d-flex justify-space-between">
      <BaseButton
        :text="`Back to 'Cover details'`"
        @click="back"
        class="font-weight-bold d-flex ml-n5"
        color="psdarkgreen"
        flat
        icon="mdi-chevron-left"
        ripple
        small
      />
      <BaseButton
        class="d-none d-lg-flex font-weight-bold px-1 py-3"
        color="psdarkgreen"
        depressed
        flat
        icon="mdi-content-save"
        ripple
        small
        text="Save &amp; exit"
        @click="toggleGlobalSaveAndExitDialog"
      />
    </div>

    <div class="d-flex align-center justify-space-between mt-10">
      <h2 class="d-none d-lg-block font-lg-28">Your Rent Protection quote</h2>
      <h2 class="d-block d-lg-none">Your quote</h2>
      <BaseButton
        class="d-flex d-lg-none font-weight-bold px-1 py-3"
        color="psdarkgreen"
        depressed
        flat
        icon="mdi-content-save"
        ripple
        small
        text="Save"
        @click="toggleGlobalSaveAndExitDialog"
      />
    </div>

    <v-row no-gutters class="d-flex justify-center mt-6 gr-4">
      <v-col class="insurance-card rounded-lg">
        <div
          v-if="isSixMonthsCover"
          class="insurance-card-header bggrey justify-center"
        >
          <h4>6 months cover</h4>
        </div>
        <div
          v-else
          class="insurance-card-header bggrey justify-center justify-xl-space-between"
        >
          <h4 class="d-none d-xl-block">12 months cover</h4>
          <PaymentFrequencySwitch
            :false-value="PAYMENT_FREQUENCY_VALUE.MONTHLY"
            :true-value="PAYMENT_FREQUENCY_VALUE.ANNUAL"
            false-label="Monthly"
            true-label="Yearly"
            v-model="paymentFrequency.Value"
          />
        </div>
        <v-divider class="bordergrey"></v-divider>
        <div class="insurance-card-body">
          <h2>Rent Protection Insurance</h2>
          <div class="min-h-150">
            <v-img
              width="237"
              :src="require('@/assets/images/arc-legal-logo.png')"
              transition="scale-transition"
              max-width="237"
              alt=""
              class="logo mt-4"
            />
            <p class="my-4 font-18">Includes Tenant Eviction cover</p>
            <span class="price">{{ price | formatCurrency }}</span>
            <h4>{{ paymentFrequencyDescription }}</h4>
            <BaseButton
              @click="showBreakdown"
              class="px-0 primary--text opacity-80"
              depressed
              flat
              link
              ripple
              text="View a breakdown"
            />
          </div>
          <BaseButton
            @click="applyForQuote()"
            class="font-20 font-weight-bold py-6 mt-6"
            color="mustard"
            depressed
            height="60"
            rounded
            text="APPLY"
            width="100%"
          />
        </div>
      </v-col>
    </v-row>
    <div class="d-flex justify-center pt-6">
      <BaseButton
        @click="shareQuote()"
        class="font-weight-bold px-1 py-3 d-flex mr-10"
        color="psdarkgreen"
        depressed
        flat
        icon="mdi-share-variant"
        ripple
        small
        :text="$vuetify.breakpoint.lgAndUp ? 'Share quote' : 'Share'"
      />
      <BaseButton
        @click="printQuote()"
        class="font-weight-bold px-1 py-3 d-flex"
        color="psdarkgreen"
        depressed
        flat
        icon="mdi-printer"
        ripple
        small
        :text="$vuetify.breakpoint.lgAndUp ? 'Print quote' : 'Print'"
      />
    </div>

    <BaseDialog
      headerIcon="mdi-share-variant"
      headerIconColor="psdarkgreen"
      ref="shareDialog"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="data?.content"
          :quoteId="data?.quoteId"
          :quoteRequestId="data?.quoteRequestId"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <BaseDialog
      headerClass="pa-4"
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdown-model="data"
          :insurancePremiumTitle="'Rent protection premium (ex IPT)'"
          :showTotalPremiumIPT="isMonthlyPayment"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <BaseDialog
      contentClass="mb-0"
      headerClass="px-7 pt-6 pb-3"
      headerDivider
      headerIcon="mdi-printer"
      headerIconColor="psdarkgreen"
      ref="printDialog"
      windowClass="pa-0 pb-xl-0 mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { scrollTop } from "@/common/utils";

import {
  INTERFACE_KEY,
  PAYMENT_FREQUENCY_VALUE,
  TERM_OF_INSURANCE_VALUE,
} from "@/constants/answer.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";

export default {
  name: "YourQuotesPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    PaymentFrequencySwitch: () =>
      import("@/components/QuoteResult/PaymentFrequencySwitch.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      PAYMENT_FREQUENCY_VALUE,
      TOOL_TIP_TEXT,
      paymentFrequency: {
        Value: PAYMENT_FREQUENCY_VALUE.ANNUAL,
      },
    };
  },
  beforeMount() {
    scrollTop();
  },
  created() {
    this.paymentFrequency = this.questionMap[INTERFACE_KEY.PAYMENT_FREQUENCY];
    if (this.isSixMonthsCover || !this.paymentFrequency.Value) {
      this.paymentFrequency.Value = PAYMENT_FREQUENCY_VALUE.ANNUAL;
    }
  },
  mounted() {
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getAnswer",
      "getCover",
      "hasQuotes",
      "insurerQuotesModel",
      "termOfInsurance",
      "getQuoteRequest",
      "httpConfig",
    ]),
    ...mapState(QUESTIONS_MODULE, [
      "commissionSacrifice",
      "overlay",
      "quoteResponse",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),

    isSixMonthsCover() {
      return this.termOfInsurance === TERM_OF_INSURANCE_VALUE.SixMonths;
    },
    isMonthlyPayment() {
      return this.paymentFrequency.Value === PAYMENT_FREQUENCY_VALUE.MONTHLY;
    },
    paymentFrequencyDescription() {
      if (this.isMonthlyPayment) {
        return "Monthly payments";
      }

      return "One off payment";
    },
    quote() {
      return this.insurerQuotesModel[0].selectedQuote;
    },
    price() {
      if (this.isMonthlyPayment) {
        return this.quote.MonthlyTotal;
      }

      return this.quote.AnnualTotal;
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "createQuote",
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "setOverlayVisible",
      "setRestoringLastStep",
      "toggleGlobalSaveAndExitDialog",
      "refreshSessionAndNextStep",
    ]),
    ...mapActions(APPLICATION_MODULE, ["setApplicationActiveQuote"]),
    back() {
      this.$router.push({
        name: ROUTE_NAME.RENT_PROTECTION.B2B.COVER_DETAILS,
      });
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    applyForQuote() {
      this.setApplicationActiveQuote(this.insurerQuotesModel[0]);
      this.refreshSessionAndNextStep(
        ROUTE_NAME.RENT_PROTECTION.B2B.APPLICATION
      );
    },
    showBreakdown() {
      if (this.isMonthlyPayment) {
        this.showPremiumBreakdownMonthly(this.quote);
      } else {
        this.showPremiumBreakdownAnnual(this.quote);
      }
    },
    async shareQuote() {
      try {
        this.setOverlayVisible(true);

        await this.saveQuoteResults();

        this.$refs.shareDialog.open("Share via email", {
          content: this.getCover,
          quoteId: this.quote.QuoteId,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async printQuote() {
      try {
        this.setOverlayVisible(true);

        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({
          docs,
          quote: this.quote,
          paymentFrequency: this.paymentFrequency.Value,
        });

        this.$refs.printDialog.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.insurance-card {
  max-width: 600px;
  border: solid var(--v-bordergrey-base) 1px;
  overflow: hidden;

  .insurance-card-header {
    height: 44px;
    display: flex;
    align-items: center;
    padding: 0 16px;
  }

  .bg-psorange-opacity-50 {
    background: rgba(250, 181, 25, 0.5);
  }

  .insurance-card-body {
    padding: 40px 27px;
  }

  .price {
    font-size: 40px;
    font-weight: 700;
    line-height: 48.76px;
  }
  .logo {
    max-width: 237px;
  }
}
</style>
